import 'core-js/modules/web.dom-collections.iterator.js';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { selectFromMap, selectFromProps, stylesFromMap, selectFromTheme } from '@thisisbud/sc-utils';
import { InputLabel, InputHint, InputError } from '@thisisbud/bui-react-typography';
import { useField } from 'formik';
import { jsxs, jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t;
const inputGroupClassName = 'bui-input-group';

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledDiv = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledDiv",
  componentId: "sc-1kavctg-0"
})(_t || (_t = _`
  position: relative;
  transition: padding-left 0.2s ease-in-out;
  ${0}

  &::before {
    content: "";
    display: block;
    position: absolute;
    height: 100%;
    left: 0;
    top: 0;
    transition: opacity 0.2s ease-in-out;
    background-color: ${0};
    width: ${0};
    opacity: ${0};
  }

  * + & {
    margin-top: ${0};
  }
`), selectFromMap(selectFromProps('$error'), {
  false: stylesFromMap({
    'padding-left': '0' // eslint-disable-line @typescript-eslint/naming-convention
  }),

  true: stylesFromMap({
    'padding-left': selectFromTheme('spacingXXSmallDefault') // eslint-disable-line @typescript-eslint/naming-convention
  })
}), selectFromTheme('colorBorderInputError'), selectFromTheme('outlineWidthInputDefault'), selectFromMap(selectFromProps('$error'), {
  false: 0,
  true: 1
}), selectFromTheme('spacingLargeDefault'));

/**
 * Get the meta data for the input field.
 *
 * @param props - The component props
 */
function useFieldMeta(props) {
  const child = React.Children.only(props.children);
  const {
    name
  } = child.props;
  const [, meta] = useField({
    name: name,
    type: 'text'
  });
  return meta;
}

/**
 * Renders an input field with a label and optional hint and error messages.
 *
 * *The `InputGroup` component needs to be a descendent of a `Form` component.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Input } from '@thisisbud/bui-react-input'
 * import { InputGroup } from '@thisisbud/bui-react-input-group'
 *
 * function Example() {
 *   return (
 *     <InputGroup label='Email address'>
 *       <Input id='example-input' name='email-address' type='email'/>
 *     </InputGroup>
 *   )
 * }
 * ```
 */
function InputGroup(props) {
  const {
    children,
    className,
    errorLabel,
    hintLabel,
    hintMessage,
    label
  } = props;
  const child = React.Children.only(children);
  const {
    disabled,
    id
  } = child.props;
  const meta = useFieldMeta(props);
  const showError = Boolean(meta.error && !disabled); // eslint-disable-line @typescript-eslint/strict-boolean-expressions
  const showHintMsg = typeof hintMessage === 'string';
  const hintMsgId = `${id}-hint-msg`;
  const errorMsgId = `${id}-error-msg`;
  const describedBy = [];
  if (showHintMsg) {
    describedBy.push(hintMsgId);
  }
  if (showError) {
    describedBy.push(errorMsgId);
  }
  return /*#__PURE__*/jsxs(StyledDiv, {
    $error: showError,
    className: classnames(inputGroupClassName, className),
    children: [/*#__PURE__*/jsx(InputLabel, {
      disabled: disabled,
      htmlFor: id,
      children: label
    }), showHintMsg ? /*#__PURE__*/jsx(InputHint, {
      hintLabel: hintLabel,
      id: hintMsgId,
      children: hintMessage
    }) : null, showError ? /*#__PURE__*/jsx(InputError, {
      errorLabel: errorLabel,
      id: errorMsgId,
      children: meta.error
    }) : null, /*#__PURE__*/React.cloneElement(child, {
      describedBy: describedBy.length > 0 ? describedBy.join(' ') : undefined,
      disabled: disabled,
      error: showError,
      id: id
    })]
  });
}
InputGroup.displayName = 'InputGroup';

export { InputGroup, inputGroupClassName };
